import React, { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

type ItemBase = {
  id: string;
  label: string;
  icon: ReactNode;
};

export type SidebarItem = ItemBase & (
  | { type: 'route'; to: string }
  | { type: 'action'; onClick: () => void }
);

type NavItemProps = {
  item: SidebarItem;
  collapsed: boolean;
  routeState?: unknown;
  onSelect: () => void;
};

const NavItem = ({ item, collapsed, routeState, onSelect }: NavItemProps) => {
  const content = (
    <>
      <Icon aria-hidden='true'>{item.icon}</Icon>
      {!collapsed && <Label>{item.label}</Label>}
    </>
  );
  const control = item.type === 'route' ? (
    <RouteRow to={item.to} state={routeState} aria-label={item.label} onClick={onSelect}>
      {content}
    </RouteRow>
  ) : (
    <ActionRow type='button' aria-label={item.label} onClick={() => {
      onSelect();
      item.onClick();
    }}>
      {content}
    </ActionRow>
  );

  return (
    <ItemContainer>
      {collapsed ? (
        <Tooltip title={item.label} placement='right' trigger={['hover', 'focus']} styles={{ root: { pointerEvents: 'none' } }}>
          {control}
        </Tooltip>
      ) : control}
    </ItemContainer>
  );
};

export default NavItem;

const ItemContainer = styled.div`
  width: 100%;
  > * { width: 100%; }
`;

const rowStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #39424a;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  &:hover { background: #f1f3f5; color: #20272d; text-decoration: none; }
  &:focus-visible { outline: 2px solid #557dec; outline-offset: -2px; }
  &[aria-current='page'] { border-left-color: #557dec; background: #edf2ff; color: #244ba7; }
`;

const RouteRow = styled(NavLink)`${rowStyles}`;
const ActionRow = styled.button`${rowStyles}`;
const Icon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  font-size: 20px;
`;
const Label = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;
